import { useEffect, useMemo, useState } from "react";
import { Search, Send, Check, Archive, Clock, Inbox } from "lucide-react";
import { api } from "@/lib/api";

const STATUSES = ["open", "replied", "waiting_on_user", "resolved", "archived"];

function StatusPill({ status }) {
  const map = {
    open:            "bg-mauve/15 text-mauve",
    replied:         "bg-sage/15 text-sage-dark",
    waiting_on_user: "bg-champagne text-cocoa",
    resolved:        "bg-cocoa/10 text-cocoa",
    archived:        "bg-ivory text-cocoa/50",
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${map[status] || ""}`}>
      {(status || "").replaceAll("_", " ")}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

export function AdminAmbassadorMessages() {
  const [messages, setMessages] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  const load = async () => {
    const status = filter === "all" ? "" : `?status=${filter}`;
    const r = await api.get(`/admin/ambassador/messages${status}`);
    setMessages(r.data?.items || []);
  };
  const loadAnalytics = async () => {
    try {
      const r = await api.get("/admin/ambassador/analytics");
      setAnalytics(r.data);
    } catch { /* */ }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); loadAnalytics(); }, [filter]);

  useEffect(() => {
    if (!activeId) { setActive(null); return; }
    api.get(`/admin/ambassador/messages/${activeId}`)
      .then((r) => setActive(r.data))
      .catch(() => setActive(null));
  }, [activeId]);

  const filtered = useMemo(() => {
    if (!q.trim()) return messages;
    const needle = q.toLowerCase();
    return messages.filter((m) =>
      (m.title || "").toLowerCase().includes(needle) ||
      (m.body || "").toLowerCase().includes(needle) ||
      (m.ambassador_email_redacted || "").toLowerCase().includes(needle),
    );
  }, [messages, q]);

  const send = async () => {
    if (!active || reply.trim().length < 2) return;
    setBusy(true);
    try {
      await api.post(`/admin/ambassador/messages/${active.id}/reply`, { body: reply });
      setReply("");
      const r = await api.get(`/admin/ambassador/messages/${active.id}`);
      setActive(r.data);
      await load();
    } finally { setBusy(false); }
  };

  const setStatus = async (status) => {
    if (!active) return;
    await api.patch(`/admin/ambassador/messages/${active.id}`, { status });
    const r = await api.get(`/admin/ambassador/messages/${active.id}`);
    setActive(r.data);
    await load();
  };

  return (
    <div data-testid="admin-amb-messages" className="space-y-6">
      <div>
        <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">Ambassador messages</p>
        <h2 className="font-heading text-3xl text-cocoa mt-1">Conversations.</h2>
      </div>

      {analytics && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <Kpi label="Active ambassadors"   value={analytics.ambassadors.active} />
          <Kpi label="Expired"              value={analytics.ambassadors.expired} />
          <Kpi label="Open"                 value={analytics.messages.by_status?.find(x => x.status==="open")?.n ?? 0} accent="text-mauve" />
          <Kpi label="Total messages"       value={analytics.messages.total} />
          <Kpi label="Invite conversion"   value={`${Math.round(analytics.invites.conversion_rate * 100)}%`} />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-5">
        {/* List */}
        <div className="ss-card-premium p-4">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-cocoa/40" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search messages…"
              data-testid="adm-amb-search"
              className="w-full bg-ivory border border-champagne rounded-md h-9 pl-8 pr-3 text-sm text-cocoa focus:border-mauve outline-none"
            />
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {["all", ...STATUSES].map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                data-testid={`adm-amb-filter-${s}`}
                className={`px-2.5 h-7 rounded-full text-[10px] uppercase tracking-wider border ${
                  filter === s ? "bg-mauve/15 border-mauve text-cocoa" : "bg-ivory border-champagne text-cocoa/60"
                }`}
              >{s.replaceAll("_", " ")}</button>
            ))}
          </div>
          <ul className="mt-4 space-y-2 max-h-[520px] overflow-y-auto">
            {filtered.map((m) => (
              <li key={m.id}>
                <button
                  onClick={() => setActiveId(m.id)}
                  data-testid={`adm-amb-row-${m.id}`}
                  className={`group w-full text-left rounded-md border p-3 transition-all ${
                    activeId === m.id ? "bg-mauve/10 border-mauve" : "bg-ivory border-champagne hover:border-mauve/60"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[10px] tracking-[0.3em] uppercase text-cocoa/55">{m.message_type.replaceAll("_", " ")}</p>
                    <div className="flex items-center gap-2">
                      {m.unread_for_admin && <span className="bg-rose w-2 h-2 rounded-full" />}
                      <StatusPill status={m.status} />
                    </div>
                  </div>
                  <p className="text-cocoa font-medium mt-1 line-clamp-1">{m.title}</p>
                  <p className="text-[11px] text-cocoa/55 mt-0.5">{m.ambassador_email_redacted} · {formatDate(m.created_at)}</p>
                </button>
              </li>
            ))}
            {filtered.length === 0 && <p className="text-cocoa/55 text-sm">No messages.</p>}
          </ul>
        </div>

        {/* Detail */}
        <div>
          {!active ? (
            <div className="ss-card-premium p-10 text-center text-cocoa/55">
              <Inbox className="mx-auto" size={20} />
              <p className="mt-3 text-sm">Pick a message to read.</p>
            </div>
          ) : (
            <div className="ss-card-premium p-6">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-mauve">{active.message_type.replaceAll("_", " ")}</p>
                  <p className="font-heading text-2xl text-cocoa mt-1">{active.title}</p>
                  <p className="text-[11px] text-cocoa/55 mt-1">From {active.ambassador_email_redacted} · {formatDate(active.created_at)}</p>
                </div>
                <StatusPill status={active.status} />
              </div>

              <p className="text-cocoa/85 text-[14px] mt-4 leading-relaxed whitespace-pre-wrap">{active.body}</p>

              {active.feedback_tags?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {active.feedback_tags.map((t) => (
                    <span key={t} className="bg-mauve/10 text-mauve px-2 py-0.5 rounded-full text-[10px] tracking-wider uppercase">{t.replaceAll("_", " ")}</span>
                  ))}
                </div>
              )}

              {active.attachments?.length > 0 && (
                <div className="mt-4">
                  <p className="text-[10px] tracking-[0.3em] uppercase text-cocoa/55">Attachments</p>
                  <ul className="mt-1 space-y-1">
                    {active.attachments.map((a) => (
                      <li key={a.id} className="text-[12px] text-cocoa/75">
                        {a.filename} · {Math.round(a.size_bytes/1024)} KB · {a.mime}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {active.thread?.length > 0 && (
                <div className="mt-5 border-t border-champagne pt-4 space-y-3">
                  {active.thread.map((t) => (
                    <div key={t.id} className={`rounded-md p-3 border ${t.author_role === "admin" ? "bg-mauve/5 border-mauve/30" : "bg-champagne/20 border-champagne"}`}>
                      <p className="text-[10px] tracking-[0.3em] uppercase mb-1 text-cocoa/55">
                        {t.author_role === "admin" ? "Admin" : "Ambassador"} · {formatDate(t.created_at)}
                      </p>
                      <p className="text-[13px] text-cocoa whitespace-pre-wrap">{t.body}</p>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-5 border-t border-champagne pt-4">
                <textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Reply…"
                  rows={3}
                  data-testid="adm-amb-reply"
                  className="w-full bg-ivory border border-champagne rounded-md p-3 text-[13px] text-cocoa focus:border-mauve outline-none"
                />
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex gap-1.5">
                    <button onClick={() => setStatus("waiting_on_user")} data-testid="adm-amb-st-waiting" className="text-[11px] uppercase tracking-wider px-2.5 h-8 rounded-full border border-champagne bg-ivory text-cocoa/65 hover:border-mauve/60 inline-flex items-center gap-1">
                      <Clock size={11} /> Waiting
                    </button>
                    <button onClick={() => setStatus("resolved")} data-testid="adm-amb-st-resolved" className="text-[11px] uppercase tracking-wider px-2.5 h-8 rounded-full border border-champagne bg-ivory text-cocoa/65 hover:border-sage inline-flex items-center gap-1">
                      <Check size={11} /> Resolve
                    </button>
                    <button onClick={() => setStatus("archived")} data-testid="adm-amb-st-archived" className="text-[11px] uppercase tracking-wider px-2.5 h-8 rounded-full border border-champagne bg-ivory text-cocoa/65 hover:border-cocoa/60 inline-flex items-center gap-1">
                      <Archive size={11} /> Archive
                    </button>
                  </div>
                  <button
                    onClick={send}
                    disabled={busy || reply.trim().length < 2}
                    data-testid="adm-amb-send"
                    className="ss-pill ss-pill-primary h-9 inline-flex items-center gap-1.5 disabled:opacity-50"
                  >
                    <Send size={12} /> Reply
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className="bg-ivory border border-champagne rounded-md p-4">
      <p className="text-[10px] tracking-[0.3em] uppercase text-cocoa/55">{label}</p>
      <p className={`font-mono text-2xl mt-1.5 ${accent || "text-cocoa"}`}>{value}</p>
    </div>
  );
}

export default AdminAmbassadorMessages;
